import tenantConstants from '@constants';
import tenantPayloads from '@payloads';
import tenantTransformers from '@transformers';


import { getClassifiedBaseURL } from '../../../utility/env';
import { convertQueryObjToString } from '../../../utility/urlQuery';


const userApiEndpoints = {
  getProfileDetailsEndpoint: {
    queryFn: async (arg, api, extraOptions, baseQuery) => {
      const userId = arg?.userId || arg;
      const apiCalls = [
        baseQuery({ url: `/api/surge/users/${userId}` }),
        baseQuery({ url: `/api/surge/languages` }),
        baseQuery({ url: `/api/surge/area_units` }),
        baseQuery({ url: `/api/surge/experience_list` }),
      ];

      const results = await Promise.allSettled(apiCalls);
      const errors = results
        .map((result, index) => {
          if (result.status === 'rejected') {
            return `API call ${index + 1} failed: ${result.reason}`;
          }
          if (result.value?.error) {
            return `API call ${index + 1} returned error: ${result.value.error}`;
          }
          return null;
        })
        .filter(Boolean);

      if (errors.length > 0) {
        return { error: errors.join(', ') };
      }

      const userResponse = results[0]?.value?.data || {};
      const languagesResponse = results[1]?.value?.data;
      const areaUnitsResponse = results[2]?.value?.data;
      const experienceListResponse = results[3]?.value?.data;

      const combinedResponse = {
        ...userResponse,
        languageList: languagesResponse?.languages || [],
        areaUnits: areaUnitsResponse?.area_units || [],
        experienceList: experienceListResponse?.experience || []
      };

      const transformedData = tenantTransformers.profileDataMapper(combinedResponse?.user);
      return { data: transformedData };
    },
  },

  updateProfileDetailsEndpoint: (data) => {
    return {
      url: `/api/surge/users/${data?.userId}`,
      method: 'PUT',
      body: tenantPayloads.updateProfilePayload(data?.body),
      transformer: (response) => {
        return response?.message || { message: 'Profile updated successfully' };
      },
    };
  },

  getAutoGeneratedUserDescription: {
    query: (data) =>
      `/api/surge/users/generate_description?${convertQueryObjToString(tenantPayloads.autoGenerateUserDescriptionPayload(data))}`,
  },

  getUserActiveLicenses: {
    query: ({ id, type }) => {
      return {
        url: type === 'Agency' ? `/api/surge/agencies/${id}/licenses` : `/api/surge/users/${id}/licenses`,
      };
    },
  },

  getUsersSavedSearches: {
    query: ({ locale }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/searches/saved/`,
        method: 'GET',
        headers: {
          'Accept-Language': locale || '',
        },
      };
    },
  },

  getFavouriteProperties: {
    query: () => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/favorites`,
        method: 'GET',
      };
    },
  },

  saveSettings: {
    query: ({ payload }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/saveSettings`,
        method: 'POST',
        body: payload,
      };
    },
  },

  deleteSavedSearch: {
    query: ({ locale, userID, id }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/${userID}/searches/saved/${id}`,
        method: 'DELETE',
        headers: {
          'Accept-Language': locale || '',
        },
      };
    },
  },

  updateLicense: {
    query: ({ licenseId, values }) => {
      return {
        url: `/api/surge/licenses/${licenseId}`,
        method: 'PATCH',
        body: values,
      };
    },
  },

  updateUserPreferences: (userId) => {
    return {
      url: `/api/surge/users/${userId}/toggle_auto_utilization`,
      method: 'PATCH',
    };
  },

  changePassword: {
    query: ({ userId, values, auth }) => {
      return {
        url: `/api/surge/users/${userId}/change_password`,
        method: 'POST',
        body: {
          user: {
            current_password: values?.oldPassword,
            new_password: values?.newPassword,
            new_password_confirmation: values?.confirmPassword,
          },
        },
        headers: {},
      };
    },
    transformer: (res) => {
      return res?.data || { message: 'Password Updated Successfully' };
    },
  },

  userPreference: {
    query: ({ id, values }) => {
      return {
        url: `/api/surge/users/${id}`,
        method: 'PUT',
        body: {
          user: {
            preferences: {
              email_notification: values?.email_notification,
              newsletter: values?.newsletter,
              automated_reports: values?.automated_reports,
              currency: values?.currency,
              area_unit: values?.area_unit,
            },
          },
        },
      };
    },
  },

  getUserSettingsDetail: {
    queryFn: async ({ id }, api, extraOptions, baseQuery) => {
      const apiCalls = [
        baseQuery({ url: `/api/surge/users/${id}` }),
        baseQuery({ url: `/api/surge/languages` }),
        baseQuery({ url: `/api/surge/area_units` }),
        baseQuery({ url: `/api/surge/experience_list` }),
      ];

      const results = await Promise.allSettled(apiCalls);
      const errors = results
        .map((result, index) => {
          if (result.status === 'rejected') {
            return `API call ${index + 1} failed: ${result.reason}`;
          }
          if (result.value?.error) {
            return `API call ${index + 1} returned error: ${result.value.error}`;
          }
          return null;
        })
        .filter(Boolean);

      if (errors.length > 0) {
        return { error: errors.join(', ') };
      }

      const userResponse = results[0]?.value?.data || {};
      const languagesResponse = results[1]?.value?.data;
      const areaUnitsResponse = results[2]?.value?.data;
      const experienceListResponse = results[3]?.value?.data;

      const combinedResponse = {
        ...userResponse,
        languageList: languagesResponse?.languages || [],
        areaUnits: areaUnitsResponse?.area_units || [],
        experienceList: experienceListResponse?.experience || []
      };

      const transformedData = tenantTransformers.settingsDetailDataMapper(combinedResponse);
      return { data: transformedData };
    },
  },

  getUserOtp: {
    query: (params) => {
      return `/api/surge/users/${params?.id}/otp?mobile_number=${params?.val}`;
    },
  },



  verifyUserOtp: {
    query: (params) => {
      return { url: `/api/surge/users/${params?.id}/verify_mobile_number`, method: 'PATCH', body: params?.values };
    },
  },

  getTruPointsCriteriaData: {
    query: () => {
      return { url: `/api/surge/users/tru_points` };
    },
  },

  getTruPointsActivityLogsData: {
    query: ({ page, userId }) => {
      return { url: `/api/surge/users/${userId}/trubroker/tru_point_activities?page=${page || 1}` };
    },
  },

  getTruBrokerLeaderboardData: {
    query: () => {
      return { url: `/api/surge/users/trubroker/leader_board` };
    },
    transformer: (response, meta, args) => {
      return tenantTransformers.truBrokerLeaderboardMapper(response);
    },
  },
};

export default userApiEndpoints;
